import { collection, doc, onSnapshot, type Firestore } from "firebase/firestore";
import {
  organizationRoleIds,
  organizationRolePermissionMap,
  type OrganizationPermissionId,
  type OrganizationRoleId,
} from "../../../packages/foundation/src/roles.js";

type Data = Record<string, unknown>;
type Stop = () => void;
export interface AuthorizedOrganization {
  id: string;
  displayName: string;
  city: string;
  brandColor: string;
  roleIds: OrganizationRoleId[];
}
export interface OrganizationAccessState {
  organizations: AuthorizedOrganization[];
  loading: boolean;
  error: string;
}

export function portalPermissions(roleIds: readonly string[]): Set<OrganizationPermissionId> {
  return new Set(
    organizationRoleIds
      .filter((role) => roleIds.includes(role))
      .flatMap((role) => organizationRolePermissionMap[role])
      .filter((permission) => permission !== "organization.readPrivate"),
  );
}

export function isActiveOwnConnection(uid: string, id: string, data: Data): boolean {
  const access = data.access as Data | undefined;
  return (
    data.organizationId === id &&
    data.personId === uid &&
    data.uid === uid &&
    data.status === "active" &&
    access?.basicConnectedPerson === true
  );
}

export function authorizedOrganization(
  uid: string,
  id: string,
  connection: Data,
  assignment: Data | undefined,
  profile: Data | undefined,
): AuthorizedOrganization | undefined {
  if (
    !isActiveOwnConnection(uid, id, connection) ||
    assignment?.organizationId !== id ||
    assignment.personId !== uid ||
    assignment.status !== "active" ||
    !Array.isArray(assignment.roleIds) ||
    !profile ||
    profile.status !== "active"
  )
    return;
  const roleIds = organizationRoleIds.filter((role) =>
    (assignment.roleIds as unknown[]).includes(role),
  );
  if (portalPermissions(roleIds).size === 0 || typeof profile.displayName !== "string") return;
  return {
    id,
    roleIds,
    displayName: profile.displayName,
    city: typeof profile.city === "string" ? profile.city : "",
    brandColor:
      typeof profile.brandColor === "string" && /^#[0-9a-f]{6}$/i.test(profile.brandColor)
        ? profile.brandColor
        : "#126568",
  };
}

export function chooseAuthorizedOrganization(
  organizations: readonly AuthorizedOrganization[],
  preferred: string | null,
): string {
  return (
    organizations.find((organization) => organization.id === preferred)?.id ??
    organizations[0]?.id ??
    ""
  );
}

export function canOpenOrganizationPage(page: string, roles: readonly string[]): boolean {
  const permissions = portalPermissions(roles);
  if (!permissions.size) return false;
  const required: Record<string, OrganizationPermissionId[]> = {
    overview: [],
    people: ["connections.review"],
    organization: ["organization.updateProfile"],
    settings: ["settings.manage"],
    "profile-audit": ["organization.updateProfile"],
    modes: ["settings.manage"],
    ramadan: ["settings.manage", "finance.read"],
    events: ["events.manage", "events.checkIn"],
    content: ["content.manage"],
    commerce: ["finance.read", "billing.read", "settings.manage"],
    spaces: ["spaces.manage"],
    volunteers: ["volunteers.manage"],
    "member-support": ["support.manage"],
    "shdeed-support": ["support.manage"],
  };
  return (
    page === "overview" ||
    (required[page]?.some((permission) => permissions.has(permission)) ?? false)
  );
}

// Candidates come exclusively from the current person's relationships. Each
// candidate requires its own canonical role and organization, confirmed online.
export function watchOrganizationAccess(input: {
  db: Firestore;
  uid: string;
  onChange: (state: OrganizationAccessState) => void;
  timeoutMs?: number;
}): Stop {
  type Candidate = {
    connection: Data;
    role?: Data;
    profile?: Data;
    roleReady: boolean;
    profileReady: boolean;
    stops: Stop[];
    error: string;
  };
  const candidates = new Map<string, Candidate>();
  let disposed = false;
  let connectionsReady = false;
  let connectionError = "";
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const emit = (): void => {
    if (disposed) return;
    const pending =
      !connectionsReady || [...candidates.values()].some((c) => !c.roleReady || !c.profileReady);
    const organizations = [...candidates]
      .flatMap(([id, c]) => {
        const organization =
          c.roleReady && c.profileReady && !c.error
            ? authorizedOrganization(input.uid, id, c.connection, c.role, c.profile)
            : undefined;
        return organization ? [organization] : [];
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName) || a.id.localeCompare(b.id));
    if (!pending) clearTimeout(timer);
    input.onChange({
      organizations,
      loading: pending && !timedOut && !connectionError,
      error:
        connectionError ||
        [...candidates.values()].find((c) => c.error)?.error ||
        (pending && timedOut ? "organization_access_timeout" : ""),
    });
  };
  const startTimer = (): void => {
    clearTimeout(timer);
    timedOut = false;
    timer = setTimeout(() => {
      timedOut = true;
      emit();
    }, input.timeoutMs ?? 15_000);
  };
  const stopCandidate = (id: string): void => {
    const candidate = candidates.get(id);
    candidates.delete(id);
    candidate?.stops.forEach((stop) => stop());
  };
  startTimer();
  const stopConnections = onSnapshot(
    collection(input.db, "organizationConnections", input.uid, "organizations"),
    { includeMetadataChanges: true },
    (snapshot) => {
      if (disposed || snapshot.metadata.fromCache) return;
      connectionsReady = true;
      connectionError = "";
      const active = new Map(
        snapshot.docs
          .filter((row) => isActiveOwnConnection(input.uid, row.id, row.data()))
          .map((row) => [row.id, row.data()]),
      );
      for (const id of candidates.keys()) if (!active.has(id)) stopCandidate(id);
      for (const [id, connection] of active) {
        const existing = candidates.get(id);
        if (existing) {
          existing.connection = connection;
          continue;
        }
        const candidate: Candidate = {
          connection,
          roleReady: false,
          profileReady: false,
          stops: [],
          error: "",
        };
        candidates.set(id, candidate);
        startTimer();
        const current = (): boolean => !disposed && candidates.get(id) === candidate;
        const fail = (): void => {
          if (!current()) return;
          candidate.role = undefined;
          candidate.profile = undefined;
          candidate.roleReady = true;
          candidate.profileReady = true;
          candidate.error = "organization_access_failed";
          emit();
        };
        candidate.stops.push(
          onSnapshot(
            doc(input.db, "organizations", id, "roleAssignments", input.uid),
            { includeMetadataChanges: true },
            (role) => {
              if (!current() || role.metadata.fromCache) return;
              candidate.role = role.data();
              candidate.roleReady = true;
              emit();
            },
            fail,
          ),
        );
        candidate.stops.push(
          onSnapshot(
            doc(input.db, "organizations", id),
            { includeMetadataChanges: true },
            (profile) => {
              if (!current() || profile.metadata.fromCache) return;
              candidate.profile = profile.data();
              candidate.profileReady = true;
              emit();
            },
            fail,
          ),
        );
      }
      emit();
    },
    () => {
      if (disposed) return;
      for (const id of candidates.keys()) stopCandidate(id);
      connectionError = "organization_access_failed";
      connectionsReady = true;
      emit();
    },
  );
  return () => {
    disposed = true;
    clearTimeout(timer);
    stopConnections();
    for (const id of candidates.keys()) stopCandidate(id);
  };
}
